import React from "react";
import { Link } from "react-router-dom";
import { PageShell, EditableSection, EditableFeatureGrid } from "./PageShell";
import { ArrowRight } from "lucide-react";

const STEPS = [
  { badge: "Step 01", title: "Choose your bottle", body: "Select any Scentodore fragrance — 50ml, 100ml, or a pure-oil roll-on. Engraving is available on every bottle size." },
  { badge: "Step 02", title: "Add engraving ($10)", body: "Type up to 20 characters — initials, a name, a date, or a short phrase. If you’d like a specific style, upload a reference image." },
  { badge: "Step 03", title: "We finish by hand", body: "Your bottle is engraved at our Canadian atelier and shipped in gift-ready packaging with a hand-written note if requested." },
];

const OCCASIONS = [
  { badge: "Birthdays", title: "A signature they’ll remember", body: "An engraved name or year turns a bottle into a keepsake." },
  { badge: "Anniversaries", title: "A shared impression", body: "Layered notes, meaningful dates — a fragrance for the two of you." },
  { badge: "Weddings", title: "Wedding party gifting", body: "Roll-ons and full-size bottles engraved for the wedding party. Bulk orders welcome." },
  { badge: "Holidays", title: "The seasonal gift", body: "Discovery sets and full-size bottles arrive gift-ready — no additional wrapping required." },
  { badge: "Thank You", title: "For someone who matters", body: "An unexpected, considered gesture that outlasts flowers or chocolates." },
  { badge: "Just Because", title: "No occasion needed", body: "Sometimes the best gift is the one given without a reason." },
];

const CORPORATE = [{
  heading: "Corporate gifting",
  body: "For teams, clients, or events — we can produce engraved runs of 10+ bottles with custom messaging. Turnaround from 10 business days. Contact us to discuss specifics.",
}];
const GIFTCARDS = [{
  heading: "Gift cards",
  body: "Prefer to let them choose their own scent? Scentodore digital gift cards are available in preset amounts of $10, $30, $50, $100, or $250 — or a custom amount up to $500 — and delivered by email on any date you pick.",
}];

export default function GiftingPage() {
  return (
    <PageShell
      slug="gifting"
      testid="gifting"
      eyebrow="Gifting & Engraving"
      title="A gift that becomes their signature."
      intro="Every Scentodore fragrance — 50ml, 100ml, or pure-oil roll-on — can be engraved with a name, initials, or short message. Ship it gift-ready with a personalised note; no additional wrapping required."
    >
      <div className="mb-14">
        <h2 className="font-serif text-xl sm:text-2xl text-[#1A1918] mb-4">How engraving works</h2>
        <EditableFeatureGrid slug="gifting" sectionKey="how_steps" fallback={STEPS} />
      </div>

      <div className="mb-14">
        <h2 className="font-serif text-xl sm:text-2xl text-[#1A1918] mb-4">For every occasion</h2>
        <EditableFeatureGrid slug="gifting" sectionKey="occasions" fallback={OCCASIONS} />
      </div>

      <EditableSection slug="gifting" sectionKey="corporate" fallback={CORPORATE} />
      <div className="-mt-8 mb-14">
        <Link
          to="/wholesale"
          data-testid="gifting-wholesale-cta"
          className="inline-flex items-center gap-2 bg-[#1A1918] text-[#FAF8F5] px-5 py-3 rounded-xl text-xs uppercase tracking-[0.18em] hover:bg-[#C5A059] hover:text-[#0B0A08] transition-colors"
        >
          Enquire about corporate gifting <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <EditableSection slug="gifting" sectionKey="giftcards" fallback={GIFTCARDS} />
      <div className="-mt-8">
        <Link
          to="/gift-cards"
          data-testid="gifting-giftcards-cta"
          className="inline-flex items-center gap-2 border border-[#1A1918]/20 text-[#1A1918] px-5 py-3 rounded-xl text-xs uppercase tracking-[0.18em] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
        >
          Buy a gift card <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </PageShell>
  );
}
